import H from '@here/maps-api-for-javascript/bin/mapsjs.bundle.harp'
import { TransportModesPanelMode } from 'components/RoutePlanner/TransportModesPanel'
import { RoutePlannerStatus, RouteWaypoint, StateRoute } from 'data/model/routePlanner'
import { RootState } from 'data/reducer'
import {
  fetchRoutes,
  setError,
  setRoutes,
  setRouteWaypointList,
  setRouteWaypoints,
  setStatus,
} from 'data/reducer/routePlanner'
import { differenceInMilliseconds } from 'date-fns'
import { supportedLanguages } from 'izumi/supportedLanguages'
import { AppEvent, logAppEvent } from 'lib/analytics'
import { config, userConfig } from 'lib/config'
import { Position } from 'lib/place'
import {
  fromIntermodalResponseRouteToStateRoute,
  fromRoutingResponseRouteToStateRoute,
  fromTransportModesPanelModeToRoutingServiceModes,
} from 'lib/route-planner/formatters'
import { IntermodalErrorResponse, IntermodalResponse } from 'map/intermodalApi'
import { createPlatform } from 'map/platform'
import { RoutingService, RoutingServiceRequestParams } from 'map/routingService'
import { createReverseGeocodeRequestParams, SearchService } from 'map/services'
import { AnyAction } from 'redux'
import { Epic, StateObservable } from 'redux-observable'
import { concat, Observable, of } from 'rxjs'
import { catchError, filter, switchMap, toArray, withLatestFrom } from 'rxjs/operators'
import { roundDecimalNumber } from 'utils/formatters'
import { getLanguageCode } from 'utils/izumi'
import { getRoutePreferences } from 'utils/storage'

const routingService = createPlatform().getRoutingService(undefined, 8) as unknown as RoutingService
const searchService = createPlatform().getSearchService() as unknown as SearchService

export const setRouteWaypointsEpic: Epic<AnyAction, AnyAction, RootState> = (
  action$: Observable<AnyAction>
) =>
  action$.pipe(
    filter(setRouteWaypoints.match),
    switchMap((action) => {
      const lang = getLanguageCode(navigator.language, supportedLanguages, userConfig.language)
      const waypoints$ = createWaypoints$(action.payload.waypoints, lang)
      return concat(...waypoints$).pipe(toArray())
    }),
    switchMap((waypoints) => of(setRouteWaypointList(waypoints)))
  )

export const fetchRoutesEpic: Epic<AnyAction, AnyAction, RootState> = (
  action$: Observable<AnyAction>,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    filter(fetchRoutes.match),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const currentTime = Date.now()
      const alternatives = 2
      const source = getRouteSource(action.payload.waypoints)
      const destination = getRouteDestination(action.payload.waypoints)
      const stops = getRouteStops(action.payload.waypoints)
      const lang = getLanguageCode(navigator.language, supportedLanguages, userConfig.language)
      let unit: 'metric' | 'imperial' = 'metric'

      if (state.app.unit === 'automatic') {
        if (state.user.countryCode === 'international') {
          unit = 'metric'
        } else {
          unit =
            state.user.countryCode === 'US' || state.user.countryCode === 'GB'
              ? 'imperial'
              : 'metric'
        }
      } else {
        unit = state.app.unit === 'metric' ? 'metric' : 'imperial'
      }

      if (!source || !destination) {
        return of(
          setError('MISSING SOURCE OR DESTINATION'),
          setStatus(RoutePlannerStatus.ROUTES_ERROR)
        )
      }

      void logAppEvent(AppEvent.RouteRequested, {
        numberOfResults: alternatives,
        numberOfstops: action.payload.waypoints.length - 2,
        transportationMode: action.payload.transportMode,
      })

      return new Observable<StateRoute[]>((observer) => {
        if (state.routePlanner.selectedTransportMode === TransportModesPanelMode.PublicTransport) {
          fetch(getIntermodalUrl(source, destination, lang, unit, alternatives))
            .then((response) => response.json())
            .then((data: IntermodalResponse) => {
              const routes = data.routes
                .filter((route) => route.sections.length !== 1)
                .map((route) => {
                  return fromIntermodalResponseRouteToStateRoute(route)
                })
              logRouteResponse(
                data.routes.length,
                action.payload.waypoints.length - 2,
                differenceInMilliseconds(Date.now(), currentTime),
                action.payload.transportMode
              )
              observer.next(routes)
              observer.complete()
            })
            .catch((error: IntermodalErrorResponse) => {
              void logAppEvent(AppEvent.RouteRequestFailed, {
                failureReason:
                  error.error !== undefined || error.error !== '' ? error.error : error.title,
                numberOfstops: action.payload.waypoints.length - 2,
                routeCalculationTime: differenceInMilliseconds(Date.now(), currentTime),
                statusCode: error.code,
              })
              observer.error(error)
            })
        } else {
          const viaList = stops
            ?.map((stop) => (stop?.lat && stop?.lng ? `${stop.lat},${stop.lng}` : ''))
            .filter((s) => s.length > 0)

          const routeServiceMode = fromTransportModesPanelModeToRoutingServiceModes(
            action.payload.transportMode
          )

          const request: RoutingServiceRequestParams = {
            alternatives,
            destination: `${destination.lat},${destination.lng}`,
            lang: lang,
            origin: `${source.lat},${source.lng}`,
            return: 'polyline,travelSummary,actions,instructions,elevation',
            transportMode: routeServiceMode,
            units: unit,
            via: new H.service.Url.MultiValueQueryParameter(viaList),
          }

          const { avoid, routingMode } = parseRoutePreferences(routeServiceMode)
          if (routingMode) request.routingMode = routingMode
          if (avoid !== null && avoid !== undefined) request['avoid[features]'] = avoid

          routingService.calculateRoute(
            request,
            (response) => {
              const routes =
                action.payload.transportMode === TransportModesPanelMode.Bike
                  ? response.routes.filter((route) =>
                      route.sections.find(
                        (section) => section.transport.mode === TransportModesPanelMode.Bike
                      )
                    )
                  : response.routes

              observer.next(
                routes.map((route) => {
                  return fromRoutingResponseRouteToStateRoute(route)
                })
              )
              logRouteResponse(
                routes.length,
                action.payload.waypoints.length - 2,
                differenceInMilliseconds(Date.now(), currentTime),
                action.payload.transportMode
              )
              observer.complete()
            },
            (error) => {
              void logAppEvent(AppEvent.RouteRequestFailed, {
                failureReason: error.cause !== undefined ? error.cause : error.message,
                numberOfstops: action.payload.waypoints.length - 2,
                routeCalculationTime: differenceInMilliseconds(Date.now(), currentTime),
                statusCode: error.status,
                transportationMode: action.payload.transportMode,
              })
              observer.error(error)
            }
          )
        }
      }).pipe(
        switchMap((data) => {
          const actionList: AnyAction[] = [
            setRoutes(data),
            setStatus(RoutePlannerStatus.ROUTES_SUCCESS),
          ]
          return of(...actionList)
        }),
        catchError((error: string) =>
          of(setError(error), setStatus(RoutePlannerStatus.ROUTES_ERROR), setRoutes([]))
        )
      )
    })
  )

const logRouteResponse = (
  numberOfResults: number,
  numberOfstops: number,
  routeCalculationTime: number,
  transportationMode: TransportModesPanelMode
): void => {
  if (numberOfResults === 0) {
    void logAppEvent(AppEvent.RouteRequestFailed, {
      failureReason: 'no route found',
      numberOfstops,
      routeCalculationTime,
      statusCode: 200,
      transportationMode,
    })
  } else {
    void logAppEvent(AppEvent.RouteRequestSucceeded, {
      numberOfResults,
      numberOfstops,
      routeCalculationTime,
      transportationMode,
    })
  }
}

const getIntermodalUrl = (
  source: Position,
  destination: Position,
  lang: string,
  units: string,
  alternatives: number
): string =>
  `https://intermodal.router.hereapi.com/v8/routes?apiKey=${config.APP_KEY}&alternatives=${alternatives}&origin=${source.lat},${source.lng}&destination=${destination.lat},${destination.lng}&lang=${lang}&units=${units}&return=actions,intermediate,polyline,fares,bookingLinks,travelSummary,incidents&rented[enable]=&rented[modes]=&taxi[enable]=&taxi[modes]=&vehicle[modes]=&via=place:parkingLot;strategy=diverseChoices`

/**
 * Remove all empty waypoints from the routeWayPoint list
 */
function sliceEmptyWaypoints(routeWaypoints: RouteWaypoint[]): RouteWaypoint[] {
  return routeWaypoints.filter((waypoint) => waypoint !== undefined && waypoint.lat && waypoint.lng)
}

/**
 * Get the first non-empty waypoint from the routeWayPoint list
 */
function getRouteSource(routeWaypoints: RouteWaypoint[]): RouteWaypoint | undefined {
  return sliceEmptyWaypoints(routeWaypoints)[0]
}

/**
 * Get the last non-empty waypoint from the routeWayPoint list
 */
function getRouteDestination(routeWaypoints: RouteWaypoint[]): RouteWaypoint | undefined {
  const slicedWaypoints = sliceEmptyWaypoints(routeWaypoints)
  return slicedWaypoints.length > 1
    ? slicedWaypoints[slicedWaypoints.length - 1]
    : ({} as RouteWaypoint)
}

/**
 * Get all intermediate non-empty stops from the routeWayPoint list
 */
const getRouteStops = (routeWaypoints: RouteWaypoint[]): RouteWaypoint[] => {
  const slicedWaypoints = sliceEmptyWaypoints(routeWaypoints)

  return slicedWaypoints.length < 3 ? [] : slicedWaypoints.slice(1).slice(0, -1)
}

const createWaypoints$ = (
  waypoints: RouteWaypoint[],
  lang: string
): Observable<RouteWaypoint>[] => {
  return waypoints.map((waypoint) => {
    return new Observable<RouteWaypoint>((observer) => {
      const hasOnlyLatLng =
        waypoint.lat !== undefined &&
        waypoint.lng !== undefined &&
        (waypoint.place === '' || waypoint.place === undefined)
      const latLng = hasOnlyLatLng ? { lat: waypoint.lat, lng: waypoint.lng } : undefined

      if (latLng !== undefined) {
        searchService.reverseGeocode(
          createReverseGeocodeRequestParams(latLng, lang),
          ({ items }) => {
            if (items.length > 0) {
              const firstCandidate = items[0]
              observer.next({
                place: firstCandidate.address.label,
                ...latLng,
              } as RouteWaypoint)
            } else {
              observer.next({
                place: `${roundDecimalNumber(latLng.lat, 5)}, ${roundDecimalNumber(latLng.lng, 5)}`,
                ...latLng,
              } as RouteWaypoint)
            }
            observer.complete()
          },
          (error) => {
            console.error('"/reversegeocode" request error', error)
            observer.error(error)
          }
        )
      } else {
        observer.next(waypoint)
        observer.complete()
      }
    })
  })
}

const parseRoutePreferences = (
  routeServiceMode: string
): { avoid: string | undefined; routingMode: 'fast' | 'short' | undefined } => {
  let avoid: string | undefined = undefined
  let routingMode: 'fast' | 'short' | undefined = undefined

  if (
    routeServiceMode === 'car' ||
    routeServiceMode === 'pedestrian' ||
    routeServiceMode === 'bicycle'
  ) {
    const routePreferences = getRoutePreferences(routeServiceMode)
    const short = routePreferences.indexOf('short')
    if (short > -1 && routeServiceMode === 'car') {
      routingMode = 'short'
      routePreferences.splice(short, 1)
    }

    if (routePreferences?.length > 0)
      avoid =
        routeServiceMode === 'pedestrian' || routeServiceMode === 'bicycle'
          ? routePreferences.filter((pref) => pref === 'ferry' || pref === 'dirtRoad').join(',')
          : routePreferences.join(',')
  }

  return { avoid, routingMode }
}
